'use client';

import React, { useState } from 'react';
import { PredictionResult } from './PredictorContent';
import PredictorSummaryChart from './PredictorSummaryChart';
import { RotateCcw, Download, MapPin, Star, TrendingUp, TrendingDown, Filter, ChevronDown, ChevronUp, Share2, BookOpen, Building2, CheckCircle2, AlertTriangle, XCircle, ArrowRight, Info } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';


interface PredictorResultsProps {
  results: PredictionResult[];
  formData: { exam: string; category: string; score: string };
  onReset: () => void;
}

type ProbabilityFilter = 'All' | 'High' | 'Medium' | 'Low';

function ProbabilityBadge({ probability }: { probability: 'High' | 'Medium' | 'Low' }) {
  const config = {
    High: {
      cls: 'probability-high',
      icon: CheckCircle2,
      label: 'High Chance',
    },
    Medium: {
      cls: 'probability-medium',
      icon: AlertTriangle,
      label: 'Medium Chance',
    },
    Low: {
      cls: 'probability-low',
      icon: XCircle,
      label: 'Low Chance',
    },
  };
  const { cls, icon: IconComp, label } = config[probability];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${cls}`}>
      <IconComp size={12} />
      {label}
    </span>
  );
}

function ScoreGapIndicator({ gap, unit, probability }: { gap: number; unit: string; probability: 'High' | 'Medium' | 'Low' }) {
  const isRank = unit === 'rank';
  const isPositive = isRank ? gap > 0 : gap >= 0;

  const label = isRank
    ? isPositive
      ? `Your rank is ${Math.abs(gap)} better than cutoff`
      : `You need rank ${Math.abs(gap)} better`
    : isPositive
    ? `${Math.abs(gap).toFixed(1)} ${unit} above cutoff`
    : `${Math.abs(gap).toFixed(1)} ${unit} below cutoff`;

  const color = probability === 'High' ? 'text-green-600' : probability === 'Medium' ? 'text-amber-600' : 'text-red-600';
  const IconComp = isPositive ? TrendingUp : TrendingDown;

  return (
    <div className={`flex items-center gap-1 text-xs font-semibold ${color}`}>
      <IconComp size={12} />
      <span>{label}</span>
    </div>
  );
}

function formatFees(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  return `₹${(amount / 1000).toFixed(0)}K`;
}

function formatPackage(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  return `₹${(amount / 1000).toFixed(0)}K`;
}

export default function PredictorResults({ results, formData, onReset }: PredictorResultsProps) {
  const [probabilityFilter, setProbabilityFilter] = useState<ProbabilityFilter>('All');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'probability' | 'nirf' | 'fees'>('probability');

  const highCount = results.filter((r) => r.probability === 'High').length;
  const medCount = results.filter((r) => r.probability === 'Medium').length;
  const lowCount = results.filter((r) => r.probability === 'Low').length;

  const filteredResults = results
    .filter((r) => probabilityFilter === 'All' || r.probability === probabilityFilter)
    .sort((a, b) => {
      if (sortBy === 'nirf') return (a.nirfRank ?? 999) - (b.nirfRank ?? 999);
      if (sortBy === 'fees') return a.totalFees - b.totalFees;
      // Default: probability order
      const order: Record<string, number> = { High: 0, Medium: 1, Low: 2 };
      return order[a.probability] - order[b.probability];
    });

  const handleDownload = () => {
    toast.success('Prediction report downloaded successfully.');
  };

  const handleShare = () => {
    toast.success('Prediction link copied to clipboard!');
  };

  return (
    <div className="animate-fade-in">
      {/* Results header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">
            Your Admission Predictions
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Based on <span className="font-semibold text-foreground">{formData.exam}</span> score of{' '}
            <span className="font-semibold text-primary font-tabular">{formData.score}</span> ·{' '}
            Category: <span className="font-semibold text-foreground">{formData.category}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleShare} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <Share2 size={14} />
            Share
          </button>
          <button onClick={handleDownload} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <Download size={14} />
            Download
          </button>
          <button onClick={onReset} className="btn-secondary flex items-center gap-1.5 text-sm py-2 px-4">
            <RotateCcw size={14} />
            New Prediction
          </button>
        </div>
      </div>

      {/* Summary stats + chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Summary cards */}
        <div className="lg:col-span-1 grid grid-cols-3 lg:grid-cols-1 gap-3">
          {[
            { label: 'High Chance', count: highCount, cls: 'probability-high', icon: CheckCircle2, desc: 'Very likely admission' },
            { label: 'Medium Chance', count: medCount, cls: 'probability-medium', icon: AlertTriangle, desc: 'Competitive, apply early' },
            { label: 'Low Chance', count: lowCount, cls: 'probability-low', icon: XCircle, desc: 'Reach colleges' },
          ].map((item) => (
            <button
              key={`summary-${item.label}`}
              onClick={() => setProbabilityFilter(
                probabilityFilter === item.label.split(' ')[0] as ProbabilityFilter
                  ? 'All' : item.label.split(' ')[0] as ProbabilityFilter
              )}
              className={`card-base p-4 text-left transition-all hover:shadow-card-hover ${
                probabilityFilter === item.label.split(' ')[0]
                  ? 'ring-2 ring-primary' :''
              }`}
            >
              <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold border mb-2 ${item.cls}`}>
                <item.icon size={11} />
                {item.label}
              </div>
              <p className="text-2xl font-extrabold text-foreground font-tabular">{item.count}</p>
              <p className="text-xs text-muted-foreground mt-0.5 hidden lg:block">{item.desc}</p>
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="lg:col-span-2 card-base p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-foreground">Probability Distribution</h3>
            <span className="text-xs text-muted-foreground font-tabular">{results.length} colleges total</span>
          </div>
          <PredictorSummaryChart high={highCount} medium={medCount} low={lowCount} />
        </div>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-5">
        <Info size={15} className="text-blue-600 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800 leading-relaxed">
          Predictions are based on 2025 official cutoff data and are indicative only. Actual 2026 cutoffs may vary. Always verify with the official college/counselling authority before applying.
        </p>
      </div>

      {/* Filter + sort bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-muted-foreground">Filter:</span>
          {(['All', 'High', 'Medium', 'Low'] as ProbabilityFilter[]).map((f) => (
            <button
              key={`prob-filter-${f}`}
              onClick={() => setProbabilityFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-all ${
                probabilityFilter === f
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
              }`}
            >
              {f} {f !== 'All' && `(${f === 'High' ? highCount : f === 'Medium' ? medCount : lowCount})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="select-base py-1.5 pl-3 pr-8 text-xs min-w-[140px]"
          >
            <option value="probability">Admission Chance</option>
            <option value="nirf">NIRF Ranking</option>
            <option value="fees">Fees: Low to High</option>
          </select>
        </div>
      </div>

      {/* Results list */}
      {filteredResults.length === 0 ? (
        <div className="card-base p-12 text-center">
          <Filter size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold text-foreground mb-1">No colleges in this category</p>
          <p className="text-sm text-muted-foreground mb-4">Try selecting a different probability filter.</p>
          <button onClick={() => setProbabilityFilter('All')} className="btn-secondary text-sm py-1.5 px-4">
            Show All Results
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredResults.map((result, index) => (
            <div
              key={`result-${result.collegeId}`}
              className={`card-base overflow-hidden transition-all animate-fade-in ${
                result.probability === 'High' ?'border-l-4 border-l-green-500'
                  : result.probability === 'Medium' ?'border-l-4 border-l-amber-500' :'border-l-4 border-l-red-400'
              }`}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {/* Rank indicator */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${
                      result.probability === 'High' ? 'bg-green-100 text-green-700' :
                      result.probability === 'Medium'? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {index + 1}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {result.nirfRank && <span className="nirf-badge">NIRF #{result.nirfRank}</span>}
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-muted-foreground bg-muted border border-border">
                          {result.ownership}
                        </span>
                      </div>
                      <h3 className="font-bold text-foreground text-base leading-tight">{result.collegeName}</h3>
                      <div className="flex items-center gap-1.5 text-muted-foreground mt-0.5">
                        <MapPin size={12} />
                        <span className="text-xs">{result.location}, {result.state}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <Star size={11} className="text-amber-400 fill-amber-400" />
                        <span className="text-xs font-semibold text-foreground">{result.rating}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <ProbabilityBadge probability={result.probability} />
                    {/* Probability bar */}
                    <div className="w-24">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs text-muted-foreground">Chance</span>
                        <span className="text-xs font-bold font-tabular text-foreground">{result.probabilityScore}%</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            result.probability === 'High' ? 'bg-green-500' :
                            result.probability === 'Medium'? 'bg-amber-500' : 'bg-red-400'
                          }`}
                          style={{ width: `${result.probabilityScore}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Cutoff ({result.cutoffExam})</p>
                    <p className="text-sm font-bold text-foreground font-tabular">
                      {result.cutoffScore}
                      <span className="text-xs font-normal text-muted-foreground ml-1">{result.cutoffUnit}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Your Score</p>
                    <p className="text-sm font-bold text-primary font-tabular">{result.yourScore}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Total Fees</p>
                    <p className="text-sm font-bold text-foreground font-tabular">{formatFees(result.totalFees)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Avg Package</p>
                    <p className="text-sm font-bold text-foreground font-tabular">{formatPackage(result.placementAvgPackage)}</p>
                  </div>
                </div>

                {/* Score gap */}
                <div className="mt-2">
                  <ScoreGapIndicator
                    gap={result.cutoffGap}
                    unit={result.cutoffUnit}
                    probability={result.probability}
                  />
                </div>

                {/* Expand button */}
                <button
                  onClick={() => setExpandedId(expandedId === result.collegeId ? null : result.collegeId)}
                  className="flex items-center gap-1.5 text-xs text-primary font-semibold mt-3 hover:opacity-80 transition-opacity"
                >
                  {expandedId === result.collegeId ? (
                    <><ChevronUp size={13} /> Hide streams & accreditation</>
                  ) : (
                    <><ChevronDown size={13} /> View streams & accreditation</>
                  )}
                </button>

                {expandedId === result.collegeId && (
                  <div className="mt-3 pt-3 border-t border-border animate-fade-in">
                    <div className="flex flex-wrap gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1.5">Streams Offered</p>
                        <div className="flex flex-wrap gap-1.5">
                          {result.streams.map((stream) => {
                            const cls =
                              stream === 'Engineering' ? 'stream-badge-engineering' :
                              stream === 'Medical' ? 'stream-badge-medical' :
                              stream === 'Management' ? 'stream-badge-management' :
                              stream === 'Law' ? 'stream-badge-law' :
                              stream === 'Arts'? 'stream-badge-arts' : 'stream-badge-commerce';
                            return (
                              <span key={`result-stream-${result.collegeId}-${stream}`} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>
                                {stream}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1.5">Accreditation</p>
                        <span className={`naac-badge ${
                          result.accreditation === 'NAAC A++' ? 'naac-a-plus-plus' :
                          result.accreditation === 'NAAC A+' ? 'naac-a-plus' :
                          result.accreditation === 'NAAC A'? 'naac-a' : 'naac-b-plus-plus'
                        }`}>
                          {result.accreditation}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Card actions */}
              <div className="px-4 py-3 bg-muted/40 border-t border-border flex items-center gap-2">
                <Link href="/" className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
                  <BookOpen size={13} />
                  View College
                </Link>
                <Link href="/" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                  <Building2 size={13} />
                  Courses & Fees
                </Link>
                <Link href="/exam-cutoff-info" className="ml-auto flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline">
                  View Cutoff History
                  <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom CTA */}
      {filteredResults.length > 0 && (
        <div className="mt-8 card-base p-6 bg-gradient-to-r from-secondary/5 to-primary/5 text-center">
          <h3 className="font-bold text-foreground text-lg mb-2">Ready to apply?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Browse detailed information about these colleges, compare them side-by-side, and download brochures.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link href="/" className="btn-primary flex items-center gap-2">
              <Building2 size={16} />
              Browse All Colleges
            </Link>
            <Link href="/exam-cutoff-info" className="btn-secondary flex items-center gap-2">
              <BookOpen size={16} />
              Check Exam Cutoffs
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}